import { TSelectionListItem } from "../types/TSelectionListItem";
import { cn } from "../../lib/utils";
import { ChevronRight, ChevronDown, Check } from "lucide-react";
import React from "react";

interface IProps {
  selectionList: TSelectionListItem[],
  hiddenList: string[]
  setHiddenList: React.Dispatch<React.SetStateAction<string[]>>
}

function CustomCheckbox({ 
  checked, 
  indeterminate,
  onChange,
  color = "#4DB4FF"
}: { 
  checked: boolean, 
  indeterminate?: boolean,
  onChange: () => void,
  color?: string
}) {
  return (
    <button
      onClick={onChange}
      className={cn(
        "w-4 h-4 rounded border-2 flex items-center justify-center transition-all duration-200 flex-shrink-0",
        checked 
          ? "border-transparent" 
          : indeterminate
            ? "border-transparent"
            : "border-slate-500 hover:border-slate-400 bg-transparent"
      )}
      style={{
        backgroundColor: checked || indeterminate ? color : 'transparent',
      }}
    >
      {checked && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
      {indeterminate && !checked && <div className="w-2 h-0.5 bg-white rounded" />}
    </button>
  );
}

function TableItem({
  item,
  isChecked,
  onToggle,
  domainColor
}: {
  item: TSelectionListItem,
  isChecked: boolean,
  onToggle: () => void,
  domainColor: string
}) {
  return (
    <div className="flex items-center gap-2 py-1.5 px-2 hover:bg-slate-800/50 rounded transition-colors">
      <CustomCheckbox 
        checked={isChecked} 
        onChange={onToggle}
        color={domainColor}
      />
      <span className="text-xs text-slate-300 truncate">{item.name}</span>
    </div>
  );
}

function DomainItem({
  item,
  index,
  hiddenList,
  onToggleDomain,
  onToggleTable,
  domainColors,
  scrollContainerRef
}: {
  item: TSelectionListItem,
  index: number,
  hiddenList: string[],
  onToggleDomain: (domain: TSelectionListItem) => void,
  onToggleTable: (table: TSelectionListItem, domain: TSelectionListItem) => void,
  domainColors: string[],
  scrollContainerRef: React.RefObject<HTMLDivElement>
}) {
  const [expanded, setExpanded] = React.useState(false);
  const domainRowRef = React.useRef<HTMLDivElement>(null);
  const domainColor = domainColors[index % domainColors.length];
  
  // Check if domain is checked (not in hidden list)
  const isDomainChecked = !hiddenList.includes(item.node_id);
  
  // Check children state for indeterminate
  const childrenCount = item.children?.length || 0;
  const visibleChildrenCount = item.children?.filter(child => 
    !hiddenList.includes(child.node_id)
  ).length || 0;
  
  const allChildrenVisible = childrenCount > 0 && visibleChildrenCount === childrenCount;
  const someChildrenVisible = visibleChildrenCount > 0 && visibleChildrenCount < childrenCount;
  
  const isChecked = isDomainChecked && allChildrenVisible;
  const isIndeterminate = isDomainChecked && someChildrenVisible;

  const hasTables = item.children && item.children.length > 0;

  const handleExpand = () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    
    // Auto-scroll after expansion
    if (newExpanded && hasTables) {
      setTimeout(() => {
        if (domainRowRef.current && scrollContainerRef.current) {
          const container = scrollContainerRef.current;
          const domainElement = domainRowRef.current;
          
          // Calculate the height needed for expanded content
          const tablesHeight = (item.children?.length || 0) * 32; // ~32px per table item
          const domainBottom = domainElement.getBoundingClientRect().bottom;
          const containerBottom = container.getBoundingClientRect().bottom;
          
          // If the expanded content would go beyond the container, scroll
          if (domainBottom + tablesHeight > containerBottom) {
            const scrollAmount = (domainBottom + tablesHeight) - containerBottom + 16;
            container.scrollBy({ top: scrollAmount, behavior: 'smooth' });
          }
        }
      }, 10);
    }
  };

  return (
    <div ref={domainRowRef} className="mb-1">
      {/* Domain Row */}
      <div className="flex items-center gap-2 py-2 px-2 hover:bg-slate-800/30 rounded-lg transition-colors">
        <CustomCheckbox 
          checked={isChecked}
          indeterminate={isIndeterminate}
          onChange={() => onToggleDomain(item)}
          color={domainColor}
        />
        
        {/* Domain Name */}
        <span className="text-sm text-slate-200 font-medium flex-1 truncate">
          {item.name}
        </span>
        
        {/* Table Count Badge */}
        {hasTables && (
          <span className="text-[10px] text-slate-500 bg-slate-800 px-1.5 py-0.5 rounded">
            {item.children?.length}
          </span>
        )}
        
        {/* Expand Button */}
        {hasTables && (
          <button 
            onClick={handleExpand}
            className="p-1 hover:bg-slate-700/50 rounded transition-colors"
          >
            {expanded ? (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-slate-400" />
            )}
          </button>
        )}
      </div>
      
      {/* Tables List (Expanded) */}
      {expanded && hasTables && (
        <div className="ml-6 mt-1 border-l border-slate-700/50 pl-2">
          {item.children?.map((table, tableIndex) => (
            <TableItem
              key={tableIndex}
              item={table}
              isChecked={!hiddenList.includes(table.node_id)}
              onToggle={() => onToggleTable(table, item)}
              domainColor={domainColor}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function SelectionFilter({
  selectionList,
  hiddenList,
  setHiddenList
}: IProps) {
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  
  // Domain colors matching the graph
  const domainColors = [
    "#FF4D4D", // Vivid Red
    "#4DFFFF", // Bright Cyan
    "#4DFFB4", // Bright Emerald
    "#FFB84D", // Bright Gold
    "#C688FF", // Bright Violet
    "#4DB4FF", // Bright Blue
    "#FF6B4D", // Bright Coral
    "#FF8844", // Bright Orange  
    "#FFE74D", // Bright Yellow
    "#B4FF4D", // Bright Lime
    "#8B88FF", // Bright Indigo
    "#FF88D4", // Bright Pink
  ];

  // Toggle domain - hides/shows domain and all its tables
  const toggleDomain = (domain: TSelectionListItem) => {
    const isCurrentlyHidden = hiddenList.includes(domain.node_id);
    
    if (isCurrentlyHidden) {
      // Show domain and all its tables
      let newHiddenList = hiddenList.filter(id => id !== domain.node_id);
      domain.children?.forEach(table => {
        newHiddenList = newHiddenList.filter(id => id !== table.node_id);
      });
      setHiddenList(newHiddenList);
    } else {
      // Hide domain and all its tables
      const newHiddenList = [...hiddenList, domain.node_id];
      domain.children?.forEach(table => {
        if (!newHiddenList.includes(table.node_id)) {
          newHiddenList.push(table.node_id);
        }
      });
      setHiddenList(newHiddenList);
    }
  };

  // Toggle table - hides/shows individual table
  const toggleTable = (table: TSelectionListItem, parentDomain: TSelectionListItem) => {
    const isCurrentlyHidden = hiddenList.includes(table.node_id);
    
    if (isCurrentlyHidden) {
      // Show table - also ensure domain is visible
      let newHiddenList = hiddenList.filter(id => id !== table.node_id && id !== parentDomain.node_id);
      setHiddenList(newHiddenList);
    } else {
      // Hide table
      const newHiddenList = [...hiddenList, table.node_id];
      
      // Check if all tables in domain are now hidden - if so, hide domain too
      const allTablesHidden = parentDomain.children?.every(t => 
        t.node_id === table.node_id || newHiddenList.includes(t.node_id)
      );
      
      if (allTablesHidden && !newHiddenList.includes(parentDomain.node_id)) {
        newHiddenList.push(parentDomain.node_id);
      }
      
      setHiddenList(newHiddenList);
    }
  };

  // Separate domains (items with children) from standalone tables
  const domains = selectionList.filter(item => item.children && item.children.length > 0);
  const standaloneTables = selectionList.filter(item => !item.children || item.children.length === 0);

  return (
    <div className="bg-slate-900/95 border border-slate-700/50 rounded-xl shadow-2xl backdrop-blur-sm min-w-[240px]">
      {/* Header */}
      <div className="px-3 py-2 border-b border-slate-700/50">
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
          Filter by Domain
        </h3>
      </div>
      
      {/* Filter List */}
      <div ref={scrollContainerRef} className="max-h-80 overflow-y-auto custom-scrollbar p-2">
        {/* Domains with Tables */}
        {domains.map((item, index) => (
          <DomainItem
            key={index}
            item={item}
            index={index}
            hiddenList={hiddenList}
            onToggleDomain={toggleDomain}
            onToggleTable={toggleTable}
            domainColors={domainColors}
            scrollContainerRef={scrollContainerRef}
          />
        ))}
        
        {/* Standalone Tables (if any) */}
        {standaloneTables.length > 0 && (
          <>
            {domains.length > 0 && (
              <div className="border-t border-slate-700/50 my-2" />
            )}
            <div className="px-2 py-1">
              <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                Other Tables
              </span>
            </div>
            {standaloneTables.map((item, index) => (
              <TableItem
                key={index}
                item={item}
                isChecked={!hiddenList.includes(item.node_id)}
                onToggle={() => {
                  if (hiddenList.includes(item.node_id)) {
                    setHiddenList(hiddenList.filter(id => id !== item.node_id));
                  } else {
                    setHiddenList([...hiddenList, item.node_id]);
                  }
                }}
                domainColor="#4DB4FF"
              />
            ))}
          </>
        )}
      </div>
    </div>
  )
}
